import React from 'react';
import { moduleApi } from '../../lib/api';
import { type ModePhotos } from '../../lib/cloturePolitique';

interface Reglages { scope_prive: string; scope_pro: string; photos_prive: ModePhotos; photos_pro: ModePhotos; auto_lien_paiement: boolean; auto_avis: boolean; role_label: string; facture_unique_passage?: boolean }

// Un seul choix à l'écran ; le type de photos suit le client : avant/après pour un particulier, vues d'ensemble pour une entreprise.
const PHOTOS_PAR_NIVEAU: Record<'obligatoires' | 'facultatives' | 'aucune', { photos_prive: ModePhotos; photos_pro: ModePhotos }> = {
    obligatoires: { photos_prive: 'avant_apres', photos_pro: 'ensemble' },
    facultatives: { photos_prive: 'facultatives', photos_pro: 'ensemble_facultatif' },
    aucune: { photos_prive: 'aucune', photos_pro: 'aucune' },
};

const champ = 'w-full bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500';

/** Carte de réglage : un titre, une phrase d'explication, puis le contenu. Même gabarit partout dans l'onglet. */
export function CarteReglage({ titre, aide, children }: { titre: string; aide?: string; children: React.ReactNode }) {
    return (
        <section className="space-y-3 p-4 rounded-2xl bg-surface-soft border border-border">
            <div>
                <h3 className="text-sm font-bold text-content">{titre}</h3>
                {aide && <p className="text-[11px] text-content-muted mt-0.5">{aide}</p>}
            </div>
            {children}
        </section>
    );
}

/**
 * Fin de prestation et envois après la prestation (2026-10-19, demande d'Emeni). Deux cartes simples, chaque choix s'enregistre
 * tout de suite. Particulier : 2 ou 3 salariés qui s'accordent, photo avant/après, tout le monde peut terminer.
 * Entreprise : une équipe nombreuse, une personne désignée termine, quelques photos d'ensemble plutôt qu'une par chaise.
 */
export function ClotureReglages() {
    const [r, setR] = React.useState<Reglages | null>(null);
    const [message, setMessage] = React.useState('');
    const [erreur, setErreur] = React.useState('');

    React.useEffect(() => {
        moduleApi.getPrestationClotureReglages().then((x: any) => { if (x && !x.error) setR(x); }).catch(() => setErreur('Chargement impossible.'));
    }, []);

    const changer = async (patch: Partial<Reglages>) => {
        if (!r) return;
        const avant = r;
        setR({ ...r, ...patch }); setMessage(''); setErreur('');
        try {
            const res: any = await moduleApi.savePrestationClotureReglages(patch);
            if (res?.error) throw new Error(res.error);
            setMessage('Enregistré.');
        } catch (e: any) { setR(avant); setErreur(e?.message || "L'enregistrement a échoué."); }
    };

    if (!r) return null;
    const niveauPhotos: 'obligatoires' | 'facultatives' | 'aucune' = r.photos_prive === 'aucune' ? 'aucune' : (r.photos_prive === 'avant_apres' ? 'obligatoires' : 'facultatives');
    return (
        <>
            <CarteReglage titre="Fin de prestation" aide="Ce que le salarié doit faire sur place avant de terminer. La signature du client est toujours demandée.">
                <div className="space-y-1.5">
                    <p className="text-[11px] font-bold text-content-muted">Photos de la prestation</p>
                    <div className="grid grid-cols-3 gap-2">
                        {([['obligatoires', 'Obligatoires'], ['facultatives', 'Facultatives'], ['aucune', 'Aucune']] as const).map(([cle, libelle]) => (
                            <button key={cle} type="button" onClick={() => changer(PHOTOS_PAR_NIVEAU[cle])}
                                className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${niveauPhotos === cle ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                {libelle}
                            </button>
                        ))}
                    </div>
                    <p className="text-[11px] text-content-muted">Une paire avant/après par article du rendez-vous (chaises, canapés…), particuliers comme entreprises ; le salarié peut ajouter des paires.</p>
                </div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" className="accent-brand-600 mt-0.5" checked={r.scope_pro === 'responsable'} onChange={(e) => changer({ scope_pro: e.target.checked ? 'responsable' : 'assignes' })} />
                    <span className="text-sm text-content"><b>Chez une entreprise, une seule personne termine la prestation</b>
                        <span className="block text-[11px] text-content-muted">Quand plusieurs salariés interviennent, seule la personne que vous désignez sur le rendez-vous prend les photos et fait signer le client.</span></span>
                </label>
                {r.scope_pro === 'responsable' && (
                    <div className="space-y-1.5 pl-6">
                        <p className="text-[11px] font-bold text-content-muted">Comment l'appelez-vous ?</p>
                        <div className="flex flex-wrap gap-1.5">
                            {['Responsable', "Chef d'équipe", 'Manager', 'Référent'].map((m) => (
                                <button key={m} type="button" onClick={() => changer({ role_label: m })}
                                    className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${r.role_label === m ? 'bg-brand-600 text-brand-fg border-transparent' : 'border-border text-content-muted hover:text-content'}`}>{m}</button>
                            ))}
                        </div>
                        <input id="role-label" className={champ} maxLength={30} defaultValue={r.role_label} key={r.role_label} aria-label="Autre nom pour cette personne"
                            onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== r.role_label) changer({ role_label: v }); }} />
                    </div>
                )}
            </CarteReglage>

            <CarteReglage titre="Après chaque prestation" aide="Le mail de fin de prestation (Paramètres > Notifications > Planning) contient déjà ces liens quand vous les y placez : ces cases ne servent que si ce mail est désactivé. Désactivé, le lien de paiement s'envoie d'un clic sur la fiche du rendez-vous.">
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" className="accent-brand-600 mt-0.5" checked={r.auto_lien_paiement} onChange={(e) => changer({ auto_lien_paiement: e.target.checked })} />
                    <span className="text-sm text-content"><b>Envoyer automatiquement le lien de paiement</b>
                        <span className="block text-[11px] text-content-muted">Seulement s'il reste quelque chose à régler. Si la facture part déjà avec la fin de prestation, elle contient ce lien : pas de doublon.</span></span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" className="accent-brand-600 mt-0.5" checked={r.auto_avis} onChange={(e) => changer({ auto_avis: e.target.checked })} />
                    <span className="text-sm text-content"><b>Envoyer automatiquement la demande d'avis</b>
                        <span className="block text-[11px] text-content-muted">Nécessite le module Avis. Une seule demande par rendez-vous.</span></span>
                </label>
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" className="accent-brand-600 mt-0.5" checked={!!r.facture_unique_passage} onChange={(e) => changer({ facture_unique_passage: e.target.checked })} />
                    <span className="text-sm text-content"><b>Une seule facture par passage</b>
                        <span className="block text-[11px] text-content-muted">Quand un passage se fait en plusieurs visites, la facture ne part qu'à la dernière visite et regroupe tout. Rien n'est encaissé sur les visites intermédiaires. Valable pour les passages d'un contrat.</span></span>
                </label>
            </CarteReglage>
            {message && <p className="text-xs font-semibold text-green-600 dark:text-green-400">{message}</p>}
            {erreur && <p className="text-xs font-semibold text-red-500">{erreur}</p>}
        </>
    );
}
